/**
 * Shared axios instance.
 *
 * - All requests go to relative `/api/*` (vite dev proxy in dev,
 *   nginx reverse proxy in prod, Caddy in front of nginx).
 * - A request interceptor injects the bearer token from localStorage.
 * - A response interceptor clears the token and bounces to /login on
 *   401 (covers expired JWTs and revoked sessions).
 */

import axios, { AxiosError, type AxiosInstance } from "axios";

import { clearToken, getToken } from "@/lib/auth";

const api: AxiosInstance = axios.create({
  baseURL: "/",
  // Long-running endpoints (CSV export, /backfill/run kick-off) are
  // still well under 2 min — anything longer is a bug.
  timeout: 120_000,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (resp) => resp,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      clearToken();
      // Avoid infinite redirects on the login page itself.
      if (window.location.pathname !== "/login") {
        const next = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.replace(`/login?next=${next}`);
      }
    }
    return Promise.reject(error);
  },
);

/** Extract a human-friendly message from an axios error. */
export function apiErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { detail?: string | { msg?: string }[] }
      | undefined;
    if (data?.detail) {
      if (typeof data.detail === "string") return data.detail;
      if (Array.isArray(data.detail)) {
        return data.detail
          .map((d) => (typeof d === "string" ? d : d.msg ?? ""))
          .filter(Boolean)
          .join(" — ");
      }
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}

export default api;
